import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui";
import type { CompoundSearchCondition, CompoundSearchField } from "~/features/compoundEntities/types";
import { cn } from "~/lib/utils";

interface FieldDef {
  value: CompoundSearchField;
  labelKey: string;
  /** When present, the field is chosen from a fixed option list instead of free text. */
  options?: { value: string; labelKey: string }[];
}

// Static t() references so i18next-extract (removeUnusedKeys) keeps the dynamically-used labelKeys.
// t("model.compoundEntity.compoundKeyType") t("model.compoundEntity.compoundKeyTypeOption.defaultWikidata")
// t("model.compoundEntity.compoundKeyTypeOption.defaultIndividual") t("model.compoundEntity.compoundKeyTypeOption.custom")
const FIELDS: FieldDef[] = [
  { value: "id", labelKey: "model.common.id" },
  { value: "key", labelKey: "model.common.key" },
  { value: "name", labelKey: "model.common.name" },
  { value: "nameJa", labelKey: "model.common.nameJa" },
  { value: "recordType", labelKey: "model.common.recordType" },
  {
    value: "compoundKeyType",
    labelKey: "model.compoundEntity.compoundKeyType",
    options: [
      { value: "default-wikidata", labelKey: "model.compoundEntity.compoundKeyTypeOption.defaultWikidata" },
      { value: "default-individual", labelKey: "model.compoundEntity.compoundKeyTypeOption.defaultIndividual" },
      { value: "custom", labelKey: "model.compoundEntity.compoundKeyTypeOption.custom" },
    ],
  },
];

interface CompoundEntitiesSearchControlsProps {
  conditions: CompoundSearchCondition[];
  onConditionsChange: (next: CompoundSearchCondition[]) => void;
  onPageReset: () => void;
}

export function CompoundEntitiesSearchControls({
  conditions,
  onConditionsChange,
  onPageReset,
}: CompoundEntitiesSearchControlsProps) {
  const { t } = useTranslation();

  const [field, setField] = useState<CompoundSearchField>("id");
  const [term, setTerm] = useState("");
  const [exactMatch, setExactMatch] = useState(true);

  const fieldDef = FIELDS.find((f) => f.value === field) ?? FIELDS[0];
  const isIdField = field === "id";
  const isOptionsField = Boolean(fieldDef.options);

  const handleFieldChange = (next: string) => {
    const f = FIELDS.find((x) => x.value === next);
    if (!f) return;
    setField(f.value);
    setTerm("");
    if (f.value === "id") setExactMatch(true);
  };

  const labelForCondition = (c: CompoundSearchCondition) => {
    const def = FIELDS.find((f) => f.value === c.field);
    const fieldLabel = t(def?.labelKey ?? "model.common.id");
    if (def?.options) {
      const opt = def.options.find((o) => o.value === c.value);
      return `${fieldLabel}: ${t(opt?.labelKey ?? c.value)}`;
    }
    const op = c.field === "id" || c.exactMatch ? "=" : "contains";
    return `${fieldLabel} ${op} “${c.value}”`;
  };

  const handleAdd = () => {
    const value = term.trim();
    if (!value) return;
    const next: CompoundSearchCondition = {
      id: `${field}-${value}-${conditions.length}`,
      field,
      value,
      exactMatch: isIdField || isOptionsField ? true : exactMatch,
    };
    onConditionsChange([...conditions, next]);
    setTerm("");
    onPageReset();
  };

  const handleRemove = (id: string) => {
    onConditionsChange(conditions.filter((c) => c.id !== id));
    onPageReset();
  };

  const handleClearAll = () => {
    onConditionsChange([]);
    onPageReset();
  };

  const currentFieldLabel = t(fieldDef.labelKey);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={field} onValueChange={handleFieldChange}>
          <SelectTrigger className="h-9 w-[180px] text-14-regular">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FIELDS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {t(f.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isOptionsField ? (
          <Select value={term} onValueChange={setTerm}>
            <SelectTrigger className="h-9 w-[360px] text-14-regular">
              <SelectValue placeholder={t("ui.searchByX", { x: currentFieldLabel })} />
            </SelectTrigger>
            <SelectContent>
              {fieldDef.options?.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            placeholder={t("ui.searchByX", { x: currentFieldLabel })}
            className="h-9 w-[360px]"
          />
        )}

        {!isOptionsField && (
          <label className="flex cursor-pointer select-none items-center gap-2 text-14-regular text-fg-2">
            <button
              type="button"
              role="switch"
              aria-checked={isIdField ? true : exactMatch}
              onClick={() => !isIdField && setExactMatch((v) => !v)}
              disabled={isIdField}
              className={cn(
                "relative inline-flex h-[22px] w-10 shrink-0 rounded-full border-2 border-transparent",
                "transition-colors duration-200 ease-in-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 focus-visible:ring-offset-1",
                "disabled:cursor-not-allowed disabled:opacity-50",
                isIdField || exactMatch ? "bg-primary-500" : "bg-fg-3/30",
              )}
            >
              <span
                className={cn(
                  "pointer-events-none inline-block h-[18px] w-[18px] rounded-full bg-white",
                  "shadow-sm ring-0 transition-transform duration-200 ease-in-out",
                  isIdField || exactMatch ? "translate-x-[18px]" : "translate-x-0",
                )}
              />
            </button>
            <span className={isIdField ? "text-fg-3" : ""}>{t("ui.exactMatch")}</span>
          </label>
        )}

        <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={!term.trim()}>
          <Plus />
          <span>{t("ui.addCondition")}</span>
        </Button>
      </div>

      {conditions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-12-regular text-fg-3">{t("ui.searchConditions")}:</span>
          {conditions.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 rounded-full border border-primary-300 bg-primary-50 px-2.5 py-0.5 text-12-regular text-primary-600"
            >
              {labelForCondition(c)}
              <button
                type="button"
                onClick={() => handleRemove(c.id)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-primary-100"
                aria-label={t("ui.delete")}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={handleClearAll}
            className="text-12-regular text-fg-3 underline-offset-2 hover:text-fg-1 hover:underline"
          >
            {t("ui.clearAll")}
          </button>
        </div>
      )}
    </div>
  );
}
